import {
  isEmpty,
  isNil,
  last,
  range,
  sortedLastIndex,
  uniq,
  zip,
} from "lodash";
import {
  getCheckboxBase,
  getChildLoopGroupKey,
  isCheckboxAnchor,
} from "../../anchors";
import { zoneBaseName } from "../../data-fields";
import { portionOf, textItems, unionRect, viewportRect } from "./pdf-geometry";
import { pageLines, pickHeadings } from "./pdf-headings";
import { openPdf, pdfOperators } from "./pdf-reader";

const TEXT_MARKER = /\{\{([^{}]*)\}\}/g;
const CHECKBOX_MARKER = /\[[A-Z][A-Za-z0-9]*\]/g;
const SAME_LINE = 0.5;
const FONT_WAIT = 1500;
const IDENTITY = [1, 0, 0, 1, 0, 0];
const PAINT_OPS = [
  "stroke",
  "closeStroke",
  "fill",
  "eoFill",
  "fillStroke",
  "eoFillStroke",
  "closeFillStroke",
  "closeEOFillStroke",
];

const markerKey = (text) => text.replace(/\s+/g, "");

const joinItems = (items) => {
  const starts = [];
  let length = 0;
  for (const item of items) {
    starts.push(length);
    length += item.str.length;
  }
  return { text: items.map((item) => item.str).join(""), starts };
};

const markerMatches = (text) => [
  ...[...text.matchAll(TEXT_MARKER)].map((match) => ({
    anchor: markerKey(match[1]),
    start: match.index,
    end: match.index + match[0].length,
  })),
  ...[...text.matchAll(CHECKBOX_MARKER)].map((match) => ({
    anchor: match[0],
    start: match.index,
    end: match.index + match[0].length,
  })),
];

const lineRects = (portions) =>
  portions.reduce((lines, portion) => {
    const previous = last(lines);
    if (
      !isNil(previous) &&
      Math.abs(previous.baseline - portion.baseline) <
        portion.height * SAME_LINE
    )
      return [...lines.slice(0, -1), unionRect(previous, portion)];
    return [...lines, portion];
  }, []);

const pageMarkers = ({ items, viewport }) => {
  const usable = textItems(items);
  const { text, starts } = joinItems(usable);
  return markerMatches(text)
    .filter(({ anchor }) => !isEmpty(anchor))
    .map(({ anchor, start, end }) => {
      const first = sortedLastIndex(starts, start) - 1;
      const final = sortedLastIndex(starts, end - 1) - 1;
      const portions = range(first, final + 1).map((index) =>
        portionOf(
          usable[index],
          index === first ? start - starts[index] : 0,
          index === final ? end - starts[index] : usable[index].str.length,
        ),
      );
      return {
        anchor,
        rects: lineRects(portions).map((rect) => viewportRect(viewport, rect)),
      };
    });
};

const loadedFont = (objects, name) =>
  new Promise((resolve) => {
    if (objects.has(name)) {
      resolve(objects.get(name));
      return;
    }
    const timer = setTimeout(() => resolve(null), FONT_WAIT);
    objects.get(name, (font) => {
      clearTimeout(timer);
      resolve(font);
    });
  });

const multiply = (m, n) => [
  m[0] * n[0] + m[2] * n[1],
  m[1] * n[0] + m[3] * n[1],
  m[0] * n[2] + m[2] * n[3],
  m[1] * n[2] + m[3] * n[3],
  m[0] * n[4] + m[2] * n[5] + m[4],
  m[1] * n[4] + m[3] * n[5] + m[5],
];

const applied = (m, [x, y]) => [
  m[0] * x + m[2] * y + m[4],
  m[1] * x + m[3] * y + m[5],
];

const isMatrix = (value) => Array.isArray(value) && value.length === 6;

const isSingleShape = (pathOps, ops) =>
  pathOps.filter((op) => op === ops.moveTo || op === ops.rectangle).length ===
  1;

const shapeRect = (matrix, [minX, maxX, minY, maxY], viewport) => {
  if (![minX, maxX, minY, maxY].every(Number.isFinite)) return null;
  const corners = [
    [minX, minY],
    [minX, maxY],
    [maxX, minY],
    [maxX, maxY],
  ].map((corner) => applied(matrix, corner));
  const xs = corners.map(([x]) => x);
  const ys = corners.map(([, y]) => y);
  return viewportRect(viewport, {
    x1: Math.min(...xs),
    y1: Math.min(...ys),
    x2: Math.max(...xs),
    y2: Math.max(...ys),
  });
};

const pathFrames = ({ fnArray, argsArray }, { ops, viewport }) => {
  const paints = new Set(PAINT_OPS.map((name) => ops[name]));
  const stack = [];
  const frames = [];
  let matrix = IDENTITY;
  let annotation = 0;
  for (const [index, fn] of fnArray.entries()) {
    const args = argsArray[index];
    if (fn === ops.beginAnnotation) annotation += 1;
    if (fn === ops.endAnnotation) annotation = Math.max(0, annotation - 1);
    if (annotation > 0 || fn === ops.endAnnotation) continue;
    if (fn === ops.save) stack.push(matrix);
    if (fn === ops.restore || fn === ops.paintFormXObjectEnd)
      matrix = stack.pop() ?? IDENTITY;
    if (fn === ops.transform && isMatrix(args)) matrix = multiply(matrix, args);
    if (fn === ops.paintFormXObjectBegin) {
      stack.push(matrix);
      if (isMatrix(args?.[0])) matrix = multiply(matrix, args[0]);
    }
    if (
      fn === ops.constructPath &&
      paints.has(fnArray[index + 1]) &&
      isSingleShape(args[0], ops)
    ) {
      const rect = shapeRect(matrix, args[2], viewport);
      if (!isNil(rect) && rect.width > 0 && rect.height > 0) frames.push(rect);
    }
  }
  return frames;
};

const pagePaint = async ({ page, items, viewport, ops }) => {
  try {
    const operators = await page.getOperatorList();
    const names = uniq(
      textItems(items)
        .map((item) => item.fontName)
        .filter((name) => !isEmpty(name)),
    );
    const fonts = await Promise.all(
      names.map((name) => loadedFont(page.commonObjs, name)),
    );
    return {
      fonts: new Map(zip(names, fonts)),
      frames: isNil(ops) ? [] : pathFrames(operators, { ops, viewport }),
    };
  } catch (_error) {
    return { fonts: new Map(), frames: [] };
  }
};

const readPage = async ({ pdf, number, ops }) => {
  const page = await pdf.getPage(number);
  const viewport = page.getViewport({ scale: 1 });
  const { items } = await page.getTextContent();
  const { fonts, frames } = await pagePaint({ page, items, viewport, ops });
  return {
    size: { width: viewport.width, height: viewport.height },
    matches: pageMarkers({ items, viewport }),
    frames,
    lines: pageLines({ items, viewport, fonts }).map((line) => ({
      ...line,
      page: number - 1,
    })),
  };
};

export const readMarkers = async (bytes) => {
  const task = await openPdf(bytes);
  try {
    const [pdf, ops] = await Promise.all([
      task.promise,
      pdfOperators().catch(() => null),
    ]);
    const read = await Promise.all(
      range(1, pdf.numPages + 1).map((number) =>
        readPage({ pdf, number, ops }),
      ),
    );
    const pages = read.map(({ size }) => size);
    const markers = read
      .flatMap(({ matches }, page) =>
        matches.map((match) => ({ ...match, page })),
      )
      .flatMap(({ anchor, page, rects }, marker) =>
        rects.map((rect, line) => ({ anchor, page, marker, line, rect })),
      );
    const headings = pickHeadings({
      pages,
      lines: read.flatMap(({ lines }) => lines),
      frames: read.map(({ frames }) => frames),
    });
    return { markers, headings, pages };
  } finally {
    task.destroy();
  }
};

const zoneIdOfAnchor = (anchor) =>
  isCheckboxAnchor(anchor)
    ? `[${getCheckboxBase(anchor)}]`
    : (getChildLoopGroupKey(anchor) ?? anchor);

export const zoneBoxes = (markers, zones) => {
  const byAnchor = new Map(
    zones.flatMap((zone) =>
      zone.anchors.map((anchor) => [markerKey(anchor), zone.id]),
    ),
  );
  const byId = new Map(zones.map((zone) => [markerKey(zone.id), zone.id]));
  const byBase = new Map(
    zones.map((zone) => [markerKey(zoneBaseName(zone.id)), zone.id]),
  );
  const zoneOf = (anchor) => {
    const id = zoneIdOfAnchor(anchor);
    return (
      byAnchor.get(anchor) ??
      byId.get(id) ??
      byBase.get(markerKey(zoneBaseName(id)))
    );
  };
  const boxes = {};
  for (const { anchor, page, marker, line, rect } of markers) {
    const zoneId = zoneOf(anchor);
    if (isNil(zoneId)) continue;
    boxes[zoneId] = [
      ...(boxes[zoneId] || []),
      { page, anchor, marker, line, ...rect },
    ];
  }
  return boxes;
};
